import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useEffect, useRef, useState } from "react";
import swal from "sweetalert";
import { FaEye, FaUserEdit } from "react-icons/fa";
import Moment from "react-moment";
import DOMPurify from "dompurify";
import { get_ApiManager, post_ApiManager } from "../ApiManager/ApiManager";
import { internalNoteByUser } from "../Services/apiServices";
import CustomPhoneInput from "../Agents/InputField";
import PermissionSwitch from "../GenericComponents/PermissionSwitches";
import TaskModalRoot from "../TaskManagement/TaskModalRoot";
import { CMS_PRIMARY } from "../TaskManagement/workspaceConstants";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  DATE_INPUT_CLASS,
  FIELD_CLASS,
  FieldError,
  INPUT_CLASS,
  LABEL_CLASS,
  SECTION_CLASS,
  calculateProbationPeriod,
  openPicker,
} from "./accountFormShared";
import EmployeeAccessRoles, {
  persistEmployeeRoleAssignment,
} from "./EmployeeAccessRoles";
import "./accounts.css";

const EMPTY_ERRORS = {
  nameError: "",
  phoneError: "",
  emailError: "",
  roleError: "",
  countryError: "",
  branchError: "",
  locationError: "",
  dateOfJoiningError: "",
  genderError: "",
  checkinTimeError: "",
};

function EditAccountModal({ open, userId, mode = "edit", onClose, onSuccess }) {
  const isView = mode === "detail";
  const scrollRef = useRef(null);
  const roleAssignmentRef = useRef(null);
  const permissions = getPermissions() || {};

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [userDetail, setUserDetail] = useState({});
  const [internalNotes, setInternalNotes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [errors, setErrors] = useState(EMPTY_ERRORS);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("");
  const [location, setLocation] = useState("");
  const [country, setCountry] = useState("");
  const [ipAddress, setIpAddress] = useState("");
  const [ipCheck, setIpCheck] = useState(0);
  const [migAccess, setMigAccess] = useState(0);
  const [eduAccess, setEduAccess] = useState(0);
  const [birthdate, setBirthDate] = useState("");
  const [aatFcctAccess, setAatFcctAccess] = useState(0);
  const [branch, setBranch] = useState("");
  const [notes, setNotes] = useState("");
  const [gender, setGender] = useState("");
  const [customCheckinTime, setCustomCheckinTime] = useState("");
  const [dateOfJoining, setDateOfJoining] = useState("");
  const [probationEndDate, setProbationEndDate] = useState("");

  useEffect(() => {
    if (!open || !userId) return;
    setErrors(EMPTY_ERRORS);
    loadBranches();
    loadUser(userId);
    loadInternalNotes(userId);
  }, [open, userId]);

  const loadBranches = () => {
    get_ApiManager(
      "",
      "getBranches",
      (res) => setBranches(res.data["data"] || []),
      (err) => console.log(err),
    );
  };

  const loadUser = (id) => {
    setFetching(true);
    get_ApiManager(
      id,
      "getUser",
      (res) => {
        const user = res.data.data?.[0];
        if (user) {
          setUserDetail(user);
          setName(user.name || "");
          setEmail(user.email || "");
          setPhone(user.mobile || "");
          setRole(user.role ?? "");
          setCountry(user.country || "");
          setIpAddress(user.ip_address || "");
          setIpCheck(user.ip_check ?? 0);
          setMigAccess(user.migrationaccess ?? 0);
          setEduAccess(user.educationaccess ?? 0);
          setAatFcctAccess(user.aataccess ?? 0);
          setLocation(user.location || "");
          setBirthDate(user.birthdate || "");
          setBranch(user.main_branch ?? "");
          setNotes(user.notes || "");
          setDateOfJoining(user.date_of_joining || "");
          setProbationEndDate(user.probation_end_date || "");
          setGender(user.gender || "");
          setCustomCheckinTime(user.custom_checkin_time || "");
        }
        setFetching(false);
      },
      (err) => {
        console.log(err);
        setFetching(false);
      },
    );
  };

  const loadInternalNotes = async (id) => {
    const result = await internalNoteByUser(id);
    if (result.error) {
      swal({ text: result.error, icon: "warning", dangerMode: true });
      setInternalNotes([]);
    } else {
      setInternalNotes(result.data?.data || []);
    }
  };

  const handleCallback = (check, keyName) => {
    if (isView) return;
    if (keyName === "ip") setIpCheck(check);
    else if (keyName === "migAccess") setMigAccess(check);
    else if (keyName === "eduAccess") setEduAccess(check);
    else setAatFcctAccess(check);
  };

  const handleValidation = () => {
    setErrors(EMPTY_ERRORS);
    const emailPattern =
      /^(("[\w-\s]+")|([\w-]+(?:\.[\w-]+)*)|("[\w-\s]+")([\w-]+(?:\.[\w-]+)*))(@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,6}(?:\.[a-z]{2})?)$)|(@\[?((25[0-5]\.|2[0-4][0-9]\.|1[0-9]{2}\.|[0-9]{1,2}\.))((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\.){2}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[0-9]{1,2})\]?$)/i;

    if (!isValidPhoneNumber(phone)) {
      setErrors((prev) => ({ ...prev, phoneError: "Invalid Phone Number" }));
      return false;
    }
    if (!name.trim()) {
      scrollRef.current?.scrollIntoView({ behavior: "smooth" });
      setErrors((prev) => ({ ...prev, nameError: "Please enter name" }));
      return false;
    }
    if (!dateOfJoining) {
      setErrors((prev) => ({
        ...prev,
        dateOfJoiningError: "Please enter date of joining",
      }));
      return false;
    }
    if (!email.trim()) {
      setErrors((prev) => ({ ...prev, emailError: "Please enter email" }));
      return false;
    }
    if (!emailPattern.test(email)) {
      setErrors((prev) => ({ ...prev, emailError: "Please enter correct email" }));
      return false;
    }
    if (!role && role !== 0) {
      setErrors((prev) => ({ ...prev, roleError: "Please select role" }));
      return false;
    }
    if (!country) {
      setErrors((prev) => ({ ...prev, countryError: "Please select country" }));
      return false;
    }
    if (!branch) {
      setErrors((prev) => ({ ...prev, branchError: "Please select branch" }));
      return false;
    }
    if (!location) {
      setErrors((prev) => ({
        ...prev,
        locationError: "Please select location",
      }));
      return false;
    }
    if (!gender) {
      setErrors((prev) => ({ ...prev, genderError: "Please select gender" }));
      return false;
    }
    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isView || !handleValidation()) return;

    const data = {
      edit_id: userId,
      name,
      phone,
      email,
      role,
      country,
      branch,
      branch_id: branch,
      allow_ip: ipCheck,
      ip_address: ipAddress,
      mig_access: migAccess,
      edu_access: eduAccess,
      aat_access: aatFcctAccess,
      location,
      birthdate,
      notes,
      date_of_joining: dateOfJoining,
      probation_end_date: probationEndDate,
      gender,
      custom_checkin_time: customCheckinTime,
    };

    setLoading(true);
    post_ApiManager(
      data,
      "editUser",
      (res) => {
        setLoading(false);
        if (res.data.error) {
          swal(" ", res.data.error, "error");
        } else {
          persistEmployeeRoleAssignment(userId, roleAssignmentRef.current).then(
            () => {
              swal("Success!", "User Updated Successfully!", "success");
              onSuccess?.();
              onClose?.();
            },
          );
        }
      },
      (err) => {
        console.log(err);
        swal("Oops!", "Something went wrong!", "error");
        setLoading(false);
      },
    );
  };

  const fieldDisabled = isView || loading || fetching;
  const showIpField = userDetail.ip_address || ipCheck == 1;

  if (!open || permissions.accounts !== 1) return null;

  return (
    <TaskModalRoot open={open}>
      <div className="add-account-modal-shell relative z-10 rounded-2xl border border-[#E5E7EB] bg-white shadow-2xl">
        <div className="add-account-modal-header relative border-b border-gray-100 px-5 py-4">
          <div
            className="absolute inset-x-0 top-0 h-[3px] rounded-t-2xl"
            style={{ backgroundColor: CMS_PRIMARY }}
          />
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <div
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white shadow-sm"
                style={{ backgroundColor: CMS_PRIMARY }}
              >
                {isView ? <FaEye size={16} /> : <FaUserEdit size={16} />}
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-bold text-[#35314d] sm:text-lg">
                  {isView ? "View Employee Account" : "Edit Employee Account"}
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">
                  {isView
                    ? "Review account details and internal notes"
                    : "Update employee details, access permissions, and branch assignment"}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-gray-400 transition hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="add-account-modal-form">
          <div ref={scrollRef} className="add-account-modal-body">
            {fetching ? (
              <div className="flex min-h-[200px] items-center justify-center">
                <span className="inline-block h-8 w-8 animate-spin rounded-full border-2 border-[#007BFF]/20 border-t-[#007BFF]" />
              </div>
            ) : (
              <>
                <div className="add-account-form-grid">
                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-name" className={LABEL_CLASS}>
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`${INPUT_CLASS} ${errors.nameError ? "border-red-300 focus:border-red-400 focus:ring-red-200" : ""}`}
                      id="edit-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Enter full name"
                      disabled={fieldDisabled}
                    />
                    <FieldError message={errors.nameError} />
                  </div>

                  <div className={`${FIELD_CLASS} add-account-phone-field`}>
                    <CustomPhoneInput
                      value={phone}
                      onChange={setPhone}
                      error={errors.phoneError}
                      placeholder="Enter phone number"
                      defaultCountry="PK"
                      required
                      label="Phone"
                      disabled={fieldDisabled}
                    />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-birthdate" className={LABEL_CLASS}>
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      className={DATE_INPUT_CLASS}
                      id="edit-birthdate"
                      value={birthdate || ""}
                      onChange={(e) => setBirthDate(e.target.value)}
                      onClick={openPicker}
                      onFocus={openPicker}
                      disabled={fieldDisabled}
                    />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-date_of_joining" className={LABEL_CLASS}>
                      Date of Joining <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      className={`${DATE_INPUT_CLASS} ${errors.dateOfJoiningError ? "border-red-300 focus:border-red-400 focus:ring-red-200" : ""}`}
                      id="edit-date_of_joining"
                      value={dateOfJoining || ""}
                      onChange={(e) => {
                        setDateOfJoining(e.target.value);
                        if (!probationEndDate) {
                          setProbationEndDate(calculateProbationPeriod(e.target.value));
                        }
                      }}
                      onClick={openPicker}
                      onFocus={openPicker}
                      disabled={fieldDisabled}
                    />
                    <FieldError message={errors.dateOfJoiningError} />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-probation_end_date" className={LABEL_CLASS}>
                      Probation Period End <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      className={DATE_INPUT_CLASS}
                      id="edit-probation_end_date"
                      value={probationEndDate || ""}
                      onChange={(e) => setProbationEndDate(e.target.value)}
                      onClick={openPicker}
                      onFocus={openPicker}
                      disabled={fieldDisabled}
                    />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-gender" className={LABEL_CLASS}>
                      Gender <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="edit-gender"
                      className={`${INPUT_CLASS} ${errors.genderError ? "border-red-300 focus:border-red-400 focus:ring-red-200" : ""}`}
                      value={gender.toLowerCase()}
                      onChange={(e) => setGender(e.target.value)}
                      disabled={fieldDisabled}
                    >
                      <option value="">Select Gender</option>
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                      <option value="other">Other</option>
                    </select>
                    <FieldError message={errors.genderError} />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-custom_checkin_time" className={LABEL_CLASS}>
                      Custom Check-in Time
                    </label>
                    <input
                      type="time"
                      className={DATE_INPUT_CLASS}
                      id="edit-custom_checkin_time"
                      value={customCheckinTime || ""}
                      onChange={(e) => setCustomCheckinTime(e.target.value)}
                      onClick={openPicker}
                      onFocus={openPicker}
                      disabled={fieldDisabled}
                    />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-email" className={LABEL_CLASS}>
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`${INPUT_CLASS} ${errors.emailError ? "border-red-300 focus:border-red-400 focus:ring-red-200" : ""}`}
                      id="edit-email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter email"
                      autoComplete="off"
                      disabled={fieldDisabled}
                    />
                    <FieldError message={errors.emailError} />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-role" className={LABEL_CLASS}>
                      User Role <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="edit-role"
                      className={`${INPUT_CLASS} ${errors.roleError ? "border-red-300 focus:border-red-400 focus:ring-red-200" : ""}`}
                      value={role}
                      onChange={(e) => setRole(e.target.value)}
                      disabled={fieldDisabled}
                    >
                      <option value="">Select Role</option>
                      <option value="0">Admin</option>
                      <option value="1">User</option>
                      <option value="2">Super Admin</option>
                    </select>
                    <FieldError message={errors.roleError} />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-country" className={LABEL_CLASS}>
                      Country <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="edit-country"
                      className={`${INPUT_CLASS} ${errors.countryError ? "border-red-300 focus:border-red-400 focus:ring-red-200" : ""}`}
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      disabled={fieldDisabled}
                    >
                      <option value="">Select Country</option>
                      <option value="Pakistan">Pakistan</option>
                      <option value="Australia">Australia</option>
                      <option value="Other">Other</option>
                    </select>
                    <FieldError message={errors.countryError} />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-branch" className={LABEL_CLASS}>
                      Branch <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="edit-branch"
                      className={`${INPUT_CLASS} ${errors.branchError ? "border-red-300 focus:border-red-400 focus:ring-red-200" : ""}`}
                      value={branch}
                      onChange={(e) => setBranch(e.target.value)}
                      disabled={fieldDisabled}
                    >
                      <option value="">Select Branch</option>
                      {branches
                        .sort((a, b) => (a.name > b.name ? 1 : -1))
                        .map((value) => (
                          <option key={value.id} value={value.id}>
                            {value.name}
                          </option>
                        ))}
                    </select>
                    <FieldError message={errors.branchError} />
                  </div>

                  <div className={FIELD_CLASS}>
                    <label htmlFor="edit-location" className={LABEL_CLASS}>
                      Location <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="edit-location"
                      className={`${INPUT_CLASS} ${errors.locationError ? "border-red-300 focus:border-red-400 focus:ring-red-200" : ""}`}
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      disabled={fieldDisabled}
                    >
                      <option value="">Select Location</option>
                      <option value="Onshore">Onshore</option>
                      <option value="Offshore">Offshore</option>
                    </select>
                    <FieldError message={errors.locationError} />
                  </div>

                  <div className="add-account-field-full">
                    <label htmlFor="edit-notes" className={LABEL_CLASS}>
                      Description
                    </label>
                    <textarea
                      className={`${INPUT_CLASS} min-h-[96px] py-2.5`}
                      id="edit-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      placeholder="Enter description"
                      disabled={fieldDisabled}
                    />
                  </div>
                </div>

                <div className="add-account-permissions-section">
                  <p className="add-account-section-title">Access & Permissions</p>
                  <EmployeeAccessRoles
                    userId={userId}
                    disabled={fieldDisabled}
                    onRolesChange={(state) => {
                      roleAssignmentRef.current = state;
                    }}
                    onLegacyAccessDerived={({ mig_access, edu_access }) => {
                      setMigAccess(mig_access);
                      setEduAccess(edu_access);
                    }}
                  />
                  <div className="add-account-permissions-grid mt-4">
                    <div className="add-account-permission-item">
                      <span>Allow IP</span>
                      <PermissionSwitch
                        generic={true}
                        keyName="ip"
                        status={ipCheck}
                        parentCallback={handleCallback}
                        disabled={isView}
                      />
                    </div>
                    <div className="add-account-permission-item">
                      <span>AAT/FCCT</span>
                      <PermissionSwitch
                        generic={true}
                        keyName="aat/fcct"
                        status={aatFcctAccess}
                        parentCallback={handleCallback}
                        disabled={isView}
                      />
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Migration and education module access is controlled by CRM roles
                    (see effective access above). Legacy flags are synced on save for
                    API compatibility.
                  </p>

                  {showIpField ? (
                    <div className="add-account-permission-ip">
                      <label htmlFor="edit-ipAddress" className={LABEL_CLASS}>
                        IP Address
                      </label>
                      <input
                        type="text"
                        className={INPUT_CLASS}
                        id="edit-ipAddress"
                        value={ipAddress}
                        onChange={(e) => setIpAddress(e.target.value)}
                        placeholder="Enter IP addresses"
                        disabled={fieldDisabled}
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Add multiple addresses separated by commas
                      </p>
                    </div>
                  ) : null}
                </div>

                <div className={`${SECTION_CLASS} mt-4`}>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Internal Notes
                  </p>
                  {internalNotes.length === 0 ? (
                    <p className="py-6 text-center text-sm text-gray-500">
                      No internal notes available.
                    </p>
                  ) : (
                    <div className="add-account-internal-notes-list">
                      {internalNotes.map((value, key) => (
                        <div key={key} className="add-account-internal-note-card">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="min-w-0 flex-1 space-y-2 text-sm text-gray-700">
                              <p>
                                <span className="font-semibold text-gray-900">
                                  Managed By:
                                </span>{" "}
                                {value.username}
                              </p>
                              <p>
                                <span className="font-semibold text-gray-900">
                                  Participants:
                                </span>{" "}
                                {value.participants?.map((v, index) => (
                                  <span key={index}>[{v}] </span>
                                ))}
                              </p>
                              <p>
                                <span className="font-semibold text-gray-900">
                                  Agenda:
                                </span>{" "}
                                {value.agenda}
                              </p>
                              <div>
                                <p className="font-semibold text-gray-900">
                                  Discussion:
                                </p>
                                <div
                                  className="mt-1 whitespace-pre-wrap text-gray-600"
                                  dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                      value.discussion_notes,
                                    ),
                                  }}
                                />
                              </div>
                            </div>
                            <div className="shrink-0 text-xs text-gray-400">
                              <Moment
                                date={value.created_at}
                                format="D MMM YYYY, hh:mm:ss"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="add-account-modal-footer flex items-center justify-end gap-2 border-t border-gray-100 bg-[#FAFBFC] px-5 py-3">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="inline-flex h-10 items-center rounded-lg border border-[#E5E7EB] bg-white px-4 text-sm font-medium text-gray-600 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              {isView ? "Close" : "Cancel"}
            </button>
            {!isView && (
              <button
                type="submit"
                disabled={loading || fetching}
                className="inline-flex h-10 items-center gap-2 rounded-lg px-5 text-sm font-semibold text-white shadow-md transition hover:brightness-95 disabled:opacity-70"
                style={{
                  backgroundColor: CMS_PRIMARY,
                  boxShadow: "0 2px 8px rgba(0, 123, 255, 0.28)",
                }}
              >
                {loading ? (
                  <>
                    <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    Updating...
                  </>
                ) : (
                  "Update Account"
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </TaskModalRoot>
  );
}

export default EditAccountModal;
